import { defineMessages } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import normalizeClassificationText from './normalizeClassificationText';
import { levelPortionMarks } from './levels';
import trigraphs from './trigraphs';
import { ParsingOperatorsEnum } from './types';
import type { ClassificationMarkError, Token } from './types';

const messages = defineMessages({
  invalidCharacter: {
    id: 'messages.invalidCharacter',
    defaultMessage: 'Invalid character: {char}',
  },
});

// there are some SCI codes here: https://en.wikipedia.org/wiki/Sensitive_compartmented_information

const isAlphabeticalOrDash = (char: string): boolean => /^[A-Z0-9-]+$/.test(char);
const isEscapeCharacters = (char: string): boolean => /^[, ]+$/.test(char);

function chunkPortionMark(portionMark: string): {
  chunks: string[];
  warnings: Array<MessageDescriptor>;
} {
  const chunks: string[] = [];
  const warnings: Array<ClassificationMarkError> = [];

  let inProgress = '';
  let isAccm = false;

  portionMark.split('').forEach((char) => {
    if (isEscapeCharacters(char)) {
      // We generally ignore escape characters, but we need to handle the case where
      // inProgress contains information that we need to push to chunks.
      // We also want to handle DISPLAY ONLY and REL TO correctly as operators.
      if (inProgress.length > 0 && inProgress !== 'DISPLAY') {
        if (inProgress.startsWith('ACCM')) isAccm = true;

        if (!isAccm) {
          chunks.push(inProgress);
          inProgress = '';
        } else {
          inProgress += char;
        }
      }
    } else if (isAlphabeticalOrDash(char)) {
      if (inProgress === ParsingOperatorsEnum.SINGLE_SLASH) {
        chunks.push(inProgress);
        inProgress = '';
      }
      inProgress += char;
    } else if (char === ParsingOperatorsEnum.SINGLE_SLASH) {
      if (inProgress === ParsingOperatorsEnum.SINGLE_SLASH) {
        isAccm = false;
        chunks.push(ParsingOperatorsEnum.DOUBLE_SLASH);
        inProgress = '';
      } else if (isAlphabeticalOrDash(inProgress)) {
        chunks.push(inProgress);
        inProgress = char;
      } else {
        if (isAccm) chunks.push(inProgress);
        inProgress = char;
      }
    } else if (char === ParsingOperatorsEnum.DASH) {
      if (inProgress.length > 0) {
        chunks.push(inProgress);
      }
      chunks.push(char);
      inProgress = '';
    } else {
      warnings.push({
        ...messages.invalidCharacter,
        values: { char },
      });
    }
  });

  if (inProgress.length > 0) {
    chunks.push(inProgress);
  }

  return { chunks, warnings };
}

export function lex(_classificationText: string): {
  tokens: Token[];
  warnings: Array<MessageDescriptor>;
} {
  const filteredText = _classificationText.replace(/[^A-Z0-9-() /]/gi, '');
  const classificationText = normalizeClassificationText(filteredText);
  const chunkedPortionMark = chunkPortionMark(classificationText);
  const chunkResult = chunkedPortionMark.chunks;
  const chunkWarnings = chunkedPortionMark.warnings;

  const warnings: Array<MessageDescriptor> = [];

  if (chunkWarnings.length > 0) warnings.push(...chunkWarnings);

  const tokens: Token[] = [];

  chunkResult.forEach((chunk) => {
    if (Object.values(ParsingOperatorsEnum).includes(chunk as ParsingOperatorsEnum)) {
      tokens.push(['operator', chunk]);
    } else if (levelPortionMarks.has(chunk)) {
      tokens.push(['classification', chunk]);
    } else if (chunk in trigraphs) {
      tokens.push(['organization', chunk]);
    } else {
      tokens.push(['unknown', chunk]);
    }
  });

  return { tokens, warnings };
}

export { chunkPortionMark, isAlphabeticalOrDash, isEscapeCharacters };
